import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Button, Paper, Skeleton, Chip, Tabs, Tab, Grid,
  useTheme, alpha, Alert,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Link as LinkIcon, PersonAdd, UploadFile, PictureAsPdfOutlined,
  OpenInNew, KeyboardArrowDown, AssignmentOutlined, HourglassEmpty,
  FactCheckOutlined, HowToRegOutlined, Grading, MoreVert, PlayCircleOutline, StopCircleOutlined, Inventory2Outlined,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useCopyLink } from '../../hooks/useCopyLink';
import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { AdmissionCycle, AdmissionCycleStats, ApplicationStatus } from '../../types/admissions';
import KpiTile from '../../components/reports/finance/KpiTile';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import StatusChip from '../../components/common/StatusChip';
import dateTimeUtils from '../../utils/dateTimeUtils';
import { useAuth } from '../../context/AuthContext';
import { formatMoneyFull } from '../../components/reports/finance/chartConfig';
import ApplicationsTab from '../../components/admissions/ApplicationsTab';
import PipelineKanban from '../../components/admissions/PipelineKanban';
import AdmitCardsTab from '../../components/admissions/AdmitCardsTab';
import MeritListTab from '../../components/admissions/MeritListTab';
import DecisionsTab from '../../components/admissions/DecisionsTab';
import ReportsTab from '../../components/admissions/ReportsTab';
import CycleSettingsTab from '../../components/admissions/settings/CycleSettingsTab';
import WalkInApplicationDialog from '../../components/admissions/dialogs/WalkInApplicationDialog';
import BulkApplicationsDialog from '../../components/admissions/BulkApplicationsDialog';
import BulkMarksDialog from '../../components/admissions/BulkMarksDialog';
import { cycleHasScoring } from '../../utils/admissionAssessment';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { BlankFormDocument } from '../../components/admissions/pdf';
import BusyIcon from '../../components/common/BusyIcon';
import CycleIntakeCard from '../../components/admissions/intake/CycleIntakeCard';
import { useCycleActions } from '../../components/admissions/useCycleActions';
import { everyClassStopped } from '../../utils/admissionClasses';

/**
 * Cycle detail page. Tabs mirror the operator workflow:
 *   • Applications — the daily hot path (list, filter, per-row drawer).
 *   • Settings — cycle configuration (fee, schedule, seats, templates).
 *   • Pipeline / Admit Cards / Merit / Decisions / Reports — placeholder
 *     chips for now; land as follow-up chunks so each gets the care it needs.
 *
 * Tab selection is URL-synced via `?tab=` so a specific view is linkable.
 */

type TabKey = 'applications' | 'settings' | 'pipeline' | 'admit-cards' | 'merit' | 'decisions' | 'reports';

const TAB_DEFS: Array<{ key: TabKey; label: string; ready: boolean }> = [
  { key: 'applications', label: 'Applications', ready: true },
  { key: 'pipeline',     label: 'Pipeline',     ready: true },
  { key: 'admit-cards',  label: 'Admit cards',  ready: true },
  { key: 'merit',        label: 'Merit',        ready: true },
  { key: 'decisions',    label: 'Decisions',    ready: true },
  { key: 'reports',      label: 'Reports',      ready: true },
  { key: 'settings',     label: 'Settings',     ready: true },
];

const AdmissionCycleDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { cycleId = '' } = useParams<{ cycleId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const copyLink = useCopyLink();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const canManageCycle = canWrite('admissions_settings');
  const [cycle, setCycle] = useState<AdmissionCycle | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const activeTab = (searchParams.get('tab') as TabKey) || 'applications';
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [marksOpen, setMarksOpen] = useState(false);
  // Bump a counter whenever a mutation lands so ApplicationsTab re-loads.
  const [refreshTick, setRefreshTick] = useState(0);

  const { currency } = useAuth();
  const [stats, setStats] = useState<AdmissionCycleStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [cycleMenu, setCycleMenu] = useState<HTMLElement | null>(null);
  const cycleActions = useCycleActions(next => {
    if (next) setCycle(next);
    else navigate('/app/admissions');
  });
  const { branding } = useSchoolBranding();
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();

  // The menu stays open, the item spinning, until the form is ready.
  const downloadBlankForm = async () => {
    if (!cycle || pdfLoading) return;
    await generatePdf(
      <BlankFormDocument
        cycle={cycle}
        branding={branding}
        meta={{
          title: 'Admission Form',
          subtitle: cycle.name,
          generatedAt: new Date().toISOString(),
        }}
      />,
      `admission-form-${cycle.name.replace(/\s+/g, '-').toLowerCase()}.pdf`,
    );
    setMoreAnchor(null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await admissionCycleService.get(cycleId);
        if (!cancelled) setCycle(data);
      } catch (err: any) {
        // Said once, on the page itself.
        if (!cancelled) setLoadError(err?.message || 'Could not load this cycle.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cycleId]);

  // Snapshot counts — reloaded whenever a walk-in / bulk upload lands.
  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    admissionCycleService.stats(cycleId)
      .then((data) => { if (!cancelled) setStats(data); })
      .catch((err: any) => {
        // A missing cycle is already explained by the page.
        if (!cancelled && err?.status !== 404) enqueueSnackbar(err?.message || 'Failed to load cycle snapshot', { variant: 'error' });
      })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [cycleId, refreshTick, enqueueSnackbar]);

  const setActiveTab = (next: TabKey) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  const copyPublicLink = async () => {
    if (!cycle?.publicToken) return;
    setMoreAnchor(null);
    await copyLink(admissionCycleService.publicApplyUrl(cycle.publicToken), 'Public apply link');
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={240} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!cycle) {
    return (
      <Box>
        <Alert severity="error">{loadError ?? 'Cycle not found.'}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/app/admissions')}>
          Back to cycles
        </Button>
      </Box>
    );
  }

  const sessionLabel = typeof cycle.sessionId === 'object' ? cycle.sessionId.name : undefined;
  const isOpen = cycle.status === 'Open';
  const publicUrl = cycle.publicToken && isOpen ? admissionCycleService.publicApplyUrl(cycle.publicToken) : null;
  // A walk-in needs the desk open and a class to apply to.
  const noClassOpen = everyClassStopped(cycle);
  const deskOpen = cycle.intakeNow.walkIn.open && !noClassOpen;
  const deskClosedReason = cycle.intakeNow.walkIn.reason ?? (noClassOpen ? 'No class is taking applications.' : '');
  const fmt = (iso?: string | null) => (iso ? dateTimeUtils.formatDateForDisplay(iso) : '—');

  const count = (...keys: ApplicationStatus[]) =>
    keys.reduce((sum, k) => sum + (stats?.byStatus[k] ?? 0), 0);
  const total = stats?.total ?? 0;
  const snapshot: Array<React.ComponentProps<typeof KpiTile>> = [
    {
      label: 'Applications',
      value: String(total),
      sub: `${count('Draft', 'Submitted')} new · not yet reviewed`,
      icon: <AssignmentOutlined />,
      tone: 'primary',
    },
    {
      label: 'Awaiting fee',
      value: String(count('AppFeePending', 'AdmFeePending')),
      sub: `${count('AppFeePending')} application · ${count('AdmFeePending')} admission`,
      icon: <HourglassEmpty />,
      tone: 'warning',
    },
    {
      label: 'In assessment',
      value: String(count('UnderReview', 'DocsRequired', 'AssessmentScheduled', 'Absent', 'Assessed')),
      sub: `${count('DocsRequired')} need documents`,
      icon: <FactCheckOutlined />,
      tone: 'info',
    },
    {
      label: 'Admitted',
      value: String(count('OfferAccepted', 'ConfirmedConditional', 'Confirmed', 'Enrolled')),
      sub: `${count('Offered')} offers awaiting reply · ${count('Enrolled')} enrolled`,
      icon: <HowToRegOutlined />,
      tone: 'success',
    },
  ];

  return (
    <Box>
      {/* ── Header — title on the left, actions on the right (Ledger layout) ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>{cycle.name}</Typography>
            <StatusChip status={{ kind: 'admissionCycle', value: cycle.status }} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {[
              sessionLabel,
              cycle.schedule?.applicationClose
                ? `Applications ${fmt(cycle.schedule?.applicationOpen)} – ${fmt(cycle.schedule.applicationClose)}`
                : `Applications from ${fmt(cycle.schedule?.applicationOpen)} · no closing date`,
              cycle.schedule?.classesBeginOn && `Classes begin ${fmt(cycle.schedule.classesBeginOn)}`,
              cycle.applicationFeeAmount
                ? `Application fee ${formatMoneyFull(cycle.applicationFeeAmount, currency)}`
                : 'No application fee',
            ].filter(Boolean).join('  ·  ')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<KeyboardArrowDown fontSize="small" />}
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                color: theme.palette.primary.main,
              },
            }}
          >
            Forms &amp; links
          </Button>
          <Menu
            anchorEl={moreAnchor}
            open={!!moreAnchor}
            onClose={() => setMoreAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            {...elegantMenuWithHintsProps}
          >
            <MenuItem onClick={downloadBlankForm}>
              <ListItemIcon><BusyIcon busy={pdfLoading}><PictureAsPdfOutlined fontSize="small" sx={{ color: 'error.main' }} /></BusyIcon></ListItemIcon>
              <ListItemText
                primary={pdfLoading ? 'Preparing…' : 'Blank admission form'}
                secondary="Printable PDF for walk-in parents"
              />
            </MenuItem>
            {isOpen && canManage && (
              <MenuItem onClick={() => { setMoreAnchor(null); setBulkOpen(true); }} disabled={!deskOpen}>
                <ListItemIcon><UploadFile fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
                <ListItemText
                  primary="Bulk upload"
                  secondary={deskOpen ? 'Import applicants from a spreadsheet' : deskClosedReason}
                />
              </MenuItem>
            )}
            {canManage && cycleHasScoring(cycle) && (
              <MenuItem onClick={() => { setMoreAnchor(null); setMarksOpen(true); }}>
                <ListItemIcon><Grading fontSize="small" sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText
                  primary="Upload marks"
                  secondary="Test, interview and criteria marks from a spreadsheet"
                />
              </MenuItem>
            )}
            {publicUrl && <Divider />}
            {publicUrl && (
              <MenuItem onClick={copyPublicLink}>
                <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Copy public apply link"
                  secondary="Share with parents to apply online"
                />
              </MenuItem>
            )}
            {publicUrl && (
              <MenuItem
                component="a"
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMoreAnchor(null)}
              >
                <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Open public form"
                  secondary="See what parents see"
                />
              </MenuItem>
            )}
          </Menu>

          {isOpen && canManage && (
            <Tooltip title={deskOpen ? '' : deskClosedReason}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PersonAdd />}
                  onClick={() => setWalkInOpen(true)}
                  disabled={!deskOpen}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  New walk-in
                </Button>
              </span>
            </Tooltip>
          )}
          {canManageCycle && cycle.status !== 'Archived' && (
            <>
              <Tooltip title="Cycle actions">
                <IconButton size="small" onClick={e => setCycleMenu(e.currentTarget)} aria-label="Cycle actions"><MoreVert fontSize="small" /></IconButton>
              </Tooltip>
              <Menu
                anchorEl={cycleMenu}
                open={!!cycleMenu}
                onClose={() => setCycleMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                {...elegantMenuWithHintsProps}
              >
                {cycle.status === 'Draft' && (
                  <MenuItem onClick={() => { setCycleMenu(null); cycleActions.request(cycle, 'open'); }}>
                    <ListItemIcon><PlayCircleOutline fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
                    <ListItemText primary="Open cycle" secondary="Start taking applications" />
                  </MenuItem>
                )}
                {cycle.status === 'Open' && (
                  <MenuItem onClick={() => { setCycleMenu(null); cycleActions.request(cycle, 'close'); }}>
                    <ListItemIcon><StopCircleOutlined fontSize="small" sx={{ color: 'warning.main' }} /></ListItemIcon>
                    <ListItemText primary="Close cycle" secondary="End the season — can be reopened" />
                  </MenuItem>
                )}
                {cycle.status === 'Closed' && (
                  <MenuItem onClick={() => { setCycleMenu(null); cycleActions.request(cycle, 'reopen'); }}>
                    <ListItemIcon><PlayCircleOutline fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
                    <ListItemText primary="Reopen cycle" secondary="Take applications again" />
                  </MenuItem>
                )}
                {cycle.status === 'Closed' && (
                  <MenuItem onClick={() => { setCycleMenu(null); cycleActions.request(cycle, 'archive'); }}>
                    <ListItemIcon><Inventory2Outlined fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Archive cycle" secondary="Put away for good" />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>
      </Box>

      <CycleIntakeCard
        cycle={cycle}
        canManage={canManageCycle}
        onChanged={setCycle}
        onReopen={() => cycleActions.request(cycle, 'reopen')}
      />

      {/* ── Snapshot ───────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Snapshot
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {snapshot.map((tile) => (
            <Grid key={tile.label} size={{ xs: 12, sm: 6, lg: 3 }}>
              <KpiTile {...tile} loading={statsLoading} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          // 'clip' (not 'hidden') keeps the rounded corners without breaking
          // position: sticky inside the tabs (the Settings side navigation).
          overflow: 'clip',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 2,
            bgcolor: alpha(theme.palette.text.primary, 0.015),
            minHeight: 48,
          }}
        >
          {TAB_DEFS.map(def => (
            <Tab
              key={def.key}
              value={def.key}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={activeTab === def.key ? 600 : 500}>
                    {def.label}
                  </Typography>
                  {!def.ready && <Chip size="small" label="Soon" sx={{ height: 20, fontSize: 10 }} variant="outlined" />}
                </Stack>
              }
              disabled={!def.ready}
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 0 }}>
          {activeTab === 'applications' && <ApplicationsTab key={`apps-${refreshTick}`} cycle={cycle} />}
          {activeTab === 'pipeline' && <PipelineKanban key={`pipe-${refreshTick}`} cycle={cycle} />}
          {activeTab === 'admit-cards' && <AdmitCardsTab key={`ac-${refreshTick}`} cycle={cycle} />}
          {activeTab === 'merit' && (
            <MeritListTab
              key={`merit-${refreshTick}`}
              cycle={cycle}
              onUploadMarks={canManage && cycleHasScoring(cycle) ? () => setMarksOpen(true) : undefined}
            />
          )}
          {activeTab === 'decisions' && <DecisionsTab key={`dec-${refreshTick}`} cycle={cycle} />}
          {activeTab === 'reports' && <ReportsTab key={`rep-${refreshTick}`} cycle={cycle} />}
          {activeTab === 'settings' && (
            <CycleSettingsTab cycle={cycle} onChanged={setCycle} />
          )}
        </Box>
      </Paper>

      {cycleActions.dialog}
      {walkInOpen && (
        <WalkInApplicationDialog
          open
          cycle={cycle}
          onCancel={() => setWalkInOpen(false)}
          onCreated={async () => {
            setWalkInOpen(false);
            setRefreshTick(t => t + 1);
          }}
          onInvited={() => setRefreshTick(t => t + 1)}
        />
      )}
      <BulkMarksDialog
        open={marksOpen}
        cycle={cycle}
        onClose={() => setMarksOpen(false)}
        onSaved={() => setRefreshTick(t => t + 1)}
      />
      {bulkOpen && (
        <BulkApplicationsDialog
          open
          cycle={cycle}
          onClose={() => setBulkOpen(false)}
          onCreated={() => setRefreshTick(t => t + 1)}
        />
      )}
    </Box>
  );
};

export default AdmissionCycleDetailPage;
